import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { KeyRound, Plus, Mail, ExternalLink, LogOut, Ticket } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  voucher_number: "", guest_name: "", tour_name: "", tour_date: "",
  pickup_time: "", pickup_location: "", group_size: "", status: "Confirmed",
  intro_message: "", traffic_note: "", itinerary_text: "",
  pricing_text: "", total: "", confirmation_number: "",
  included_text: "Private vehicle and professional driver-guide\nHotel luggage drop-off assistance\nComplimentary bottled water\nFlexible itinerary adjustments based on your preferences\nAll taxes and fees",
  not_included_text: "Meals, gratuities, and personal expenses",
  notes_text: "",
  cancellation_policy: "All bookings are final. No refunds are offered unless the cancellation is due to an error or failure on our part. In such cases, a full refund or rescheduling will be provided at our discretion.",
  closing_message: ""
};

const parseLines = (text) => text.split("\n").map((l) => l.trim()).filter(Boolean);

const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]";

function KeyGate({ onUnlock }) {
  const [key, setKey] = useState("");
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4" data-testid="admin-key-gate">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
        <div className="flex items-center gap-3 mb-4">
          <KeyRound className="w-8 h-8 text-[#C9A227]" />
          <h1 className="text-xl font-bold">Admin Access</h1>
        </div>
        <p className="text-sm text-gray-600 mb-4">Enter your admin key to manage tour vouchers.</p>
        <input
          type="password" value={key} onChange={(e) => setKey(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && key && onUnlock(key)}
          placeholder="Admin key" className={inputCls} data-testid="admin-key-input"
        />
        <button
          onClick={() => key && onUnlock(key)}
          className="mt-4 w-full bg-[#002868] text-white py-2.5 rounded-lg font-semibold hover:bg-[#001d4d] transition-colors"
          data-testid="admin-key-submit"
        >
          Unlock
        </button>
      </div>
    </div>
  );
}

function VoucherRow({ v, adminKey }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const sendEmail = async () => {
    if (!email) { toast.error("Enter the customer's email first"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/vouchers/${v.voucher_number}/email`,
        { recipient_email: email, voucher_url: `${window.location.origin}/voucher/${v.voucher_number}` },
        { headers: { "X-Admin-Key": adminKey } });
      toast.success(`Voucher emailed to ${email}`);
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-wrap items-center gap-3" data-testid={`voucher-row-${v.voucher_number}`}>
      <div className="flex-1 min-w-[200px]">
        <p className="font-mono font-bold text-sm text-[#002868]">{v.voucher_number}</p>
        <p className="text-sm text-gray-700">{v.guest_name} · {v.tour_date}</p>
      </div>
      <Link to={`/voucher/${v.voucher_number}`} target="_blank"
        className="inline-flex items-center gap-1 text-sm text-[#002868] font-semibold hover:underline"
        data-testid={`voucher-view-${v.voucher_number}`}>
        <ExternalLink className="w-4 h-4" /> View
      </Link>
      <div className="flex items-center gap-2">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="customer@email.com" className={`${inputCls} w-52`}
          data-testid={`voucher-email-input-${v.voucher_number}`} />
        <button onClick={sendEmail} disabled={sending}
          className="inline-flex items-center gap-1.5 bg-[#B22234] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#8f1b2a] disabled:opacity-50 transition-colors"
          data-testid={`voucher-email-send-${v.voucher_number}`}>
          <Mail className="w-4 h-4" /> {sending ? "Sending..." : "Email"}
        </button>
      </div>
    </div>
  );
}

export default function AdminVouchers() {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem("oht_admin_key") || "");
  const [vouchers, setVouchers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadVouchers = useCallback(async (key) => {
    try {
      const res = await axios.get(`${API}/vouchers`, { headers: { "X-Admin-Key": key } });
      setVouchers(res.data);
      return true;
    } catch {
      toast.error("Invalid admin key");
      sessionStorage.removeItem("oht_admin_key");
      setAdminKey("");
      return false;
    }
  }, []);

  useEffect(() => {
    if (adminKey) loadVouchers(adminKey);
  }, [adminKey, loadVouchers]);

  const unlock = async (key) => {
    sessionStorage.setItem("oht_admin_key", key);
    setAdminKey(key);
  };

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const createVoucher = async (e) => {
    e.preventDefault();
    const itinerary = parseLines(form.itinerary_text).map((line) => {
      const idx = line.indexOf("|");
      return idx > -1
        ? { time: line.slice(0, idx).trim(), activity: line.slice(idx + 1).trim() }
        : { time: "", activity: line };
    });
    const pricing = parseLines(form.pricing_text).map((line) => {
      const idx = line.lastIndexOf("|");
      return idx > -1
        ? { label: line.slice(0, idx).trim(), amount: line.slice(idx + 1).trim() }
        : { label: line, amount: "" };
    });
    const payload = {
      voucher_number: form.voucher_number.trim(),
      guest_name: form.guest_name.trim(),
      tour_name: form.tour_name.trim(),
      tour_date: form.tour_date.trim(),
      pickup_time: form.pickup_time.trim(),
      pickup_location: form.pickup_location.trim(),
      group_size: form.group_size.trim() || "To be confirmed",
      status: form.status,
      intro_message: form.intro_message.trim() || null,
      traffic_note: form.traffic_note.trim() || null,
      itinerary,
      pricing,
      total: form.total.trim() || null,
      confirmation_number: form.confirmation_number.trim() || null,
      included: parseLines(form.included_text),
      not_included: parseLines(form.not_included_text),
      notes: parseLines(form.notes_text),
      cancellation_policy: form.cancellation_policy.trim() || null,
      closing_message: form.closing_message.trim() || null
    };
    setSaving(true);
    try {
      await axios.post(`${API}/vouchers`, payload, { headers: { "X-Admin-Key": adminKey } });
      toast.success(`Voucher ${payload.voucher_number} created!`);
      setForm(emptyForm);
      loadVouchers(adminKey);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create voucher");
    } finally {
      setSaving(false);
    }
  };

  if (!adminKey) return (
    <>
      <Helmet><title>Admin | Official Hollywood Tours</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <Toaster position="top-right" richColors />
      <KeyGate onUnlock={unlock} />
    </>
  );

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4" data-testid="admin-vouchers-page">
      <Helmet><title>Voucher Admin | Official Hollywood Tours</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <Toaster position="top-right" richColors />
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-black text-[#002868] flex items-center gap-2"><Ticket className="w-7 h-7 text-[#C9A227]" /> Voucher Admin</h1>
          <button onClick={() => { sessionStorage.removeItem("oht_admin_key"); setAdminKey(""); }}
            className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#B22234]" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Lock
          </button>
        </div>

        {/* Create form */}
        <form onSubmit={createVoucher} className="bg-white rounded-xl shadow p-6 mb-8" data-testid="voucher-create-form">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2"><Plus className="w-5 h-5 text-[#C9A227]" /> Create New Voucher</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="text-xs font-bold text-gray-500 uppercase">Voucher Number *</label>
              <input required value={form.voucher_number} onChange={set("voucher_number")} placeholder="V2H-OHT-20261231-XX" className={inputCls} data-testid="form-voucher-number" /></div>
            <div><label className="text-xs font-bold text-gray-500 uppercase">Guest Name *</label>
              <input required value={form.guest_name} onChange={set("guest_name")} placeholder="Jane Doe" className={inputCls} data-testid="form-guest-name" /></div>
            <div className="sm:col-span-2"><label className="text-xs font-bold text-gray-500 uppercase">Tour Name *</label>
              <input required value={form.tour_name} onChange={set("tour_name")} placeholder="Private Los Angeles Sightseeing Tour" className={inputCls} data-testid="form-tour-name" /></div>
            <div><label className="text-xs font-bold text-gray-500 uppercase">Tour Date *</label>
              <input required value={form.tour_date} onChange={set("tour_date")} placeholder="December 31, 2026" className={inputCls} data-testid="form-tour-date" /></div>
            <div><label className="text-xs font-bold text-gray-500 uppercase">Pick-up Time *</label>
              <input required value={form.pickup_time} onChange={set("pickup_time")} placeholder="9:30 AM" className={inputCls} data-testid="form-pickup-time" /></div>
            <div><label className="text-xs font-bold text-gray-500 uppercase">Pick-up Location *</label>
              <input required value={form.pickup_location} onChange={set("pickup_location")} placeholder="Hotel lobby, address..." className={inputCls} data-testid="form-pickup-location" /></div>
            <div><label className="text-xs font-bold text-gray-500 uppercase">Group Size</label>
              <input value={form.group_size} onChange={set("group_size")} placeholder="4 travelers" className={inputCls} data-testid="form-group-size" /></div>
            <div><label className="text-xs font-bold text-gray-500 uppercase">Status</label>
              <select value={form.status} onChange={set("status")} className={inputCls} data-testid="form-status">
                <option>Confirmed</option><option>Pending</option><option>Paid</option>
              </select></div>
            <div className="sm:col-span-2"><label className="text-xs font-bold text-gray-500 uppercase">Itinerary (one stop per line, format: time | activity)</label>
              <textarea rows={5} value={form.itinerary_text} onChange={set("itinerary_text")} placeholder={"9:30 AM | Pick-up at hotel\n11:00 AM | Hollywood Boulevard & TCL Chinese Theatre"} className={inputCls} data-testid="form-itinerary" /></div>
            <div className="sm:col-span-2"><label className="text-xs font-bold text-gray-500 uppercase">Pricing (one line per item, format: label | amount)</label>
              <textarea rows={3} value={form.pricing_text} onChange={set("pricing_text")} placeholder={"Private Tour — $285.00 per person x 4 | $1,140.00\nReservation fee & fuel surcharge | $65.00\nLuggage fee | TBD"} className={inputCls} data-testid="form-pricing" /></div>
            <div><label className="text-xs font-bold text-gray-500 uppercase">Total (e.g. $1,205.00)</label>
              <input value={form.total} onChange={set("total")} placeholder="$1,205.00" className={inputCls} data-testid="form-total" /></div>
            <div><label className="text-xs font-bold text-gray-500 uppercase">Confirmation # (from V2H portal, optional)</label>
              <input value={form.confirmation_number} onChange={set("confirmation_number")} placeholder="OHT-260809-XXXXXX" className={inputCls} data-testid="form-confirmation-number" /></div>
            <div><label className="text-xs font-bold text-gray-500 uppercase">Included (one per line)</label>
              <textarea rows={5} value={form.included_text} onChange={set("included_text")} className={inputCls} data-testid="form-included" /></div>
            <div><label className="text-xs font-bold text-gray-500 uppercase">Not Included (one per line)</label>
              <textarea rows={5} value={form.not_included_text} onChange={set("not_included_text")} className={inputCls} data-testid="form-not-included" /></div>
            <div className="sm:col-span-2"><label className="text-xs font-bold text-gray-500 uppercase">Important Notes (one per line)</label>
              <textarea rows={3} value={form.notes_text} onChange={set("notes_text")} className={inputCls} data-testid="form-notes" /></div>
            <div className="sm:col-span-2"><label className="text-xs font-bold text-gray-500 uppercase">Intro Message (optional)</label>
              <textarea rows={3} value={form.intro_message} onChange={set("intro_message")} className={inputCls} data-testid="form-intro" /></div>
            <div className="sm:col-span-2"><label className="text-xs font-bold text-gray-500 uppercase">Traffic / Advisory Note (optional)</label>
              <textarea rows={2} value={form.traffic_note} onChange={set("traffic_note")} className={inputCls} data-testid="form-traffic-note" /></div>
            <div className="sm:col-span-2"><label className="text-xs font-bold text-gray-500 uppercase">Cancellation Policy</label>
              <textarea rows={2} value={form.cancellation_policy} onChange={set("cancellation_policy")} className={inputCls} data-testid="form-cancellation" /></div>
            <div className="sm:col-span-2"><label className="text-xs font-bold text-gray-500 uppercase">Closing Message (optional)</label>
              <textarea rows={2} value={form.closing_message} onChange={set("closing_message")} className={inputCls} data-testid="form-closing" /></div>
          </div>
          <button type="submit" disabled={saving}
            className="mt-5 bg-[#002868] text-white px-8 py-2.5 rounded-full font-semibold hover:bg-[#001d4d] disabled:opacity-50 transition-colors"
            data-testid="form-submit-voucher">
            {saving ? "Creating..." : "Create Voucher"}
          </button>
        </form>

        {/* Voucher list */}
        <h2 className="text-lg font-bold mb-3" data-testid="voucher-list-heading">Existing Vouchers ({vouchers.length})</h2>
        <div className="space-y-3" data-testid="voucher-list">
          {vouchers.map((v) => <VoucherRow key={v.voucher_number} v={v} adminKey={adminKey} />)}
        </div>
      </div>
    </div>
  );
}
